/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unused-vars */
import * as React from "react";
import GeneralPopoverWrapper from "../Common/GeneralPopoverWrapper";
import Box from "@mui/material/Box";
import {
  AllCompanyAllowedSpecialPermissions,
  IPermissionStatus,
  IUser,
  LimitedPermissions,
  SelectedCompanyAllowedSpecialPermissions,
  SpecialPermissions,
  SpecialPermissionToLimitedPermissions,
} from "../../../types/user";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import { useAppContext } from "../../../contexts/useStorageContext";
import { UserRoles } from "../../../types/auth";
import { ICompany } from "../../../types/company";
import { Tooltip } from "@mui/material";
import Dialog from "@mui/material/Dialog";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import CloseIcon from "@mui/icons-material/Close";

export default function SpecialPermissionPopover({
  currentPermissions,
  onChangePermissions,
  triggerElement,
  targetUser,
  targetCompany,
  asDialog,
  dialogMaxWidth = "md",
}: {
  currentPermissions: IPermissionStatus[];
  onChangePermissions: (changed: IPermissionStatus[]) => void;
  triggerElement: JSX.Element;
  targetUser?: IUser;
  targetCompany?: ICompany;
  asDialog?: boolean;
  dialogMaxWidth?: "sm" | "md" | "lg" | "xl";
}) {
  const { state } = useAppContext();
  const { loggedInUserInfo, myCompanyProfile } = state;
  const [open, setOpen] = React.useState(false);

  const editor = (
        <Box sx={{ minWidth: asDialog ? undefined : "350px", p: 1 }}>
          {(loggedInUserInfo?.role == UserRoles.superadmin &&
          (!targetUser || targetUser?.role == UserRoles.superadmin)
            ? Object.values(SpecialPermissions)
            : [
                ...(targetUser ? AllCompanyAllowedSpecialPermissions : []),
                ...(targetUser
                  ? targetCompany?.permissions
                      ?.filter((p) => p.read)
                      .map((p) => p.permissionName)
                      .filter(
                        (p) => !AllCompanyAllowedSpecialPermissions?.includes(p)
                      ) || []
                  : SelectedCompanyAllowedSpecialPermissions),
              ]
          ).map((p) => {
            const currentPermission = currentPermissions.find(
              (cp) => cp.permissionName == p
            );
            const isSuperAdminOnly = ![
              ...AllCompanyAllowedSpecialPermissions,
              ...SelectedCompanyAllowedSpecialPermissions,
            ].includes(p);
            const isTargetUserSuperAdmin =
              targetUser?.role == UserRoles.superadmin;
            const isWriteDisallowedForTargetUserCompany =
              !AllCompanyAllowedSpecialPermissions.includes(p) &&
              !!targetUser &&
              !targetCompany?.permissions?.find((pp) => pp.permissionName == p)
                ?.write;
            return (
              <Box
                sx={{
                  borderBottom: 1,
                  borderColor: "divider",
                  p: 1,
                  bgcolor: (theme) =>
                    isSuperAdminOnly && !isTargetUserSuperAdmin
                      ? theme.palette.mode === "dark"
                        ? "rgba(255, 255, 255, 0.06)"
                        : theme.palette.action.hover
                      : "transparent",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  {p}
                  {isSuperAdminOnly ? (
                    <Box
                      sx={{
                        fontSize: "13px",
                        marginLeft: "8px",
                        color: "warning.main",
                      }}
                    >
                      Super Admin Only
                    </Box>
                  ) : null}
                </div>
                <div style={{ display: "flex", alignItems: "center" }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        disabled={isSuperAdminOnly && !isTargetUserSuperAdmin}
                        onChange={(e, checked) => {
                          if (
                            currentPermissions
                              ?.map((cp) => cp.permissionName)
                              .includes(p)
                          ) {
                            onChangePermissions(
                              currentPermissions.map((cp) => {
                                if (cp.permissionName == p) {
                                  return {
                                    ...cp,
                                    read: checked,
                                    write: !checked ? false : cp.write,
                                  };
                                }
                                return cp;
                              })
                            );
                          } else {
                            onChangePermissions([
                              ...currentPermissions.filter(
                                (cp) => cp.permissionName != p
                              ),
                              { permissionName: p, read: checked },
                            ]);
                          }
                        }}
                        checked={currentPermission?.read || false}
                      />
                    }
                    label="Read All"
                  />
                  <Tooltip
                    title={
                      isWriteDisallowedForTargetUserCompany
                        ? `User's company ${targetCompany?.name} doesn't have this permission`
                        : ""
                    }
                    arrow
                    placement="right"
                  >
                    <FormControlLabel
                      disabled={isWriteDisallowedForTargetUserCompany}
                      control={
                        <Checkbox
                          disabled={isSuperAdminOnly && !isTargetUserSuperAdmin}
                          onChange={(e, checked) => {
                            if (
                              currentPermissions
                                ?.map((cp) => cp.permissionName)
                                .includes(p)
                            ) {
                              onChangePermissions(
                                currentPermissions.map((cp) => {
                                  if (cp.permissionName == p) {
                                    return {
                                      ...cp,
                                      write: checked,
                                      read: checked ? true : cp.read,
                                    };
                                  }
                                  return cp;
                                })
                              );
                            } else {
                              onChangePermissions([
                                ...currentPermissions.filter(
                                  (cp) => cp.permissionName != p
                                ),
                                {
                                  permissionName: p,
                                  read: true,
                                  write: checked,
                                },
                              ]);
                            }
                          }}
                          checked={
                            isWriteDisallowedForTargetUserCompany
                              ? false
                              : currentPermission?.write || false
                          }
                        />
                      }
                      label="Write All"
                    />
                  </Tooltip>
                  {(SpecialPermissionToLimitedPermissions as any)[p]?.map(
                    (lp: LimitedPermissions) => {
                      const disabled =
                        !AllCompanyAllowedSpecialPermissions.includes(p) &&
                        !!targetUser &&
                        !targetCompany?.permissions
                          ?.find((pp) => pp.permissionName == p)
                          ?.limitedPermissions?.includes(lp);
                      return (
                        <Tooltip
                          title={
                            disabled
                              ? `User's company ${targetCompany?.name} doesn't have this permission`
                              : ""
                          }
                          arrow
                          placement="right"
                        >
                          <FormControlLabel
                            disabled={disabled}
                            control={
                              <Checkbox
                                disabled={
                                  isSuperAdminOnly && !isTargetUserSuperAdmin
                                }
                                onChange={(e, checked) => {
                                  if (
                                    currentPermissions
                                      ?.map((cp) => cp.permissionName)
                                      .includes(p)
                                  ) {
                                    onChangePermissions(
                                      currentPermissions.map((cp) => {
                                        if (cp.permissionName == p) {
                                          return {
                                            ...cp,
                                            limitedPermissions: checked
                                              ? [
                                                  ...(cp.limitedPermissions ||
                                                    []),
                                                  lp,
                                                ]
                                              : (
                                                  cp.limitedPermissions || []
                                                ).filter((lp) => lp != lp),
                                          };
                                        }
                                        return cp;
                                      })
                                    );
                                  } else {
                                    onChangePermissions([
                                      ...currentPermissions.filter(
                                        (cp) => cp.permissionName != p
                                      ),
                                      {
                                        permissionName: p,
                                        limitedPermissions: checked ? [lp] : [],
                                      },
                                    ]);
                                  }
                                }}
                                checked={
                                  disabled
                                    ? false
                                    : currentPermission?.limitedPermissions?.includes(
                                        lp
                                      ) || false
                                }
                              />
                            }
                            label={lp}
                          />
                        </Tooltip>
                      );
                    }
                  )}
                </div>
              </Box>
            );
          })}
        </Box>
  );

  if (asDialog) {
    return (
      <>
        <Box
          onClick={() => setOpen(true)}
          sx={{ display: "flex", alignItems: "center" }}
        >
          {triggerElement}
        </Box>
        <Dialog
          open={open}
          onClose={() => setOpen(false)}
          maxWidth={dialogMaxWidth}
          fullWidth
          scroll="paper"
        >
          <Box
            p={2}
            pb={0}
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="h6">Special Permissions</Typography>
            <IconButton onClick={() => setOpen(false)}>
              <CloseIcon />
            </IconButton>
          </Box>
          <Box sx={{ maxHeight: "70vh", overflow: "auto" }}>{editor}</Box>
        </Dialog>
      </>
    );
  }

  return (
    <GeneralPopoverWrapper
      anchorHorizontal="left"
      transformHorizontal="left"
      triggerElement={triggerElement}
      popoverContent={editor}
      tooltipTitle="Edit Special Permission"
    />
  );
}
